<template>
  <div
    class="mb-3 rounded-xl border border-neutral-200 bg-white py-2 px-5 shadow-sm"
  >
    <!-- Initialization Warning -->
    <div
      v-if="initializationFailed"
      class="mb-4 rounded-lg border border-accent-one-300 bg-accent-one-100 p-3"
    >
      <div class="flex items-center gap-2">
        <svg
          class="h-4 w-4 text-accent-one-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          ></path>
        </svg>
        <p class="text-sm text-accent-one-800">
          Connection issues detected - some features may not work properly
        </p>
      </div>
    </div>

    <!-- Header with minimalist status -->
    <div class="flex items-center justify-between">
      <h1 class="text-2xl font-semibold text-primary-500">
        Hello {{ agentName }}
      </h1>

      <button
        class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium cursor-pointer transition-colors hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        :class="[isConnected ? 'text-accent-two-800' : 'text-accent-three-500']"
        :title="statusTooltip"
        @click="toggleDetails"
      >
        <span
          class="h-2 w-2 rounded-full"
          :class="[isConnected ? 'bg-accent-two-500' : 'bg-accent-three-500']"
        ></span>
        <span>{{ isConnected ? 'Connected' : 'Disconnected' }}</span>
        <svg
          class="h-4 w-4 transition-transform"
          :class="{ 'rotate-180': showDetails }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>
    </div>

    <!-- Detailed Status (expandable) -->
    <div v-if="showDetails" class="mt-4 pt-4 border-t border-neutral-100">
      <div class="grid gap-2 text-sm text-neutral-700 sm:grid-cols-3">
        <p>
          <span class="font-medium text-secondary-800">Conversatie ID:</span>
          <span class="ml-1 text-neutral-600">{{
            conversationId || 'Niet beschikbaar'
          }}</span>
        </p>
        <p>
          <span class="font-medium text-secondary-800">Taal:</span>
          <span class="ml-1 text-neutral-600">{{
            language || 'Niet beschikbaar'
          }}</span>
        </p>
        <p class="flex items-center gap-1.5">
          <span class="font-medium text-secondary-800">Verbinding status:</span>
          <span
            class="ml-1 inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-xs"
            :class="[
              isConnected
                ? 'bg-accent-two-100 text-accent-two-800'
                : 'bg-accent-three-100 text-accent-three-800',
            ]"
          >
            <span
              class="h-1.5 w-1.5 rounded-full"
              :class="[
                isConnected ? 'bg-accent-two-500' : 'bg-accent-three-500',
              ]"
            ></span>
            Genesys: {{ genesysStatus ? 'Verbonden' : 'Verbroken' }} | CuCa AI:
            {{ backendStatus ? 'Verbonden' : 'Verbroken' }}
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

// Props
interface Props {
  agentName?: string;
  conversationId?: string;
  language?: string;
  genesysStatus: boolean;
  backendStatus: boolean;
  initializationFailed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  agentName: '',
  conversationId: '',
  language: '',
  initializationFailed: false,
});

// Local state
const showDetails = ref(false);

// Computed properties
const isConnected = computed(() => props.genesysStatus && props.backendStatus);

const statusTooltip = computed(() => {
  if (isConnected.value) {
    return 'All connections active';
  }
  const issues = [];
  if (!props.genesysStatus) issues.push('Genesys');
  if (!props.backendStatus) issues.push('Backend');
  return `Connection issues: ${issues.join(', ')}`;
});

// Methods
const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};
</script>
